<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import TicketTypesAPI from 'dashboard/api/ticketTypes';
import TicketsAPI from 'dashboard/api/tickets';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import TicketLifecycleDrawer from './TicketLifecycleDrawer.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const teams = useMapGetter('teams/getTeams');
const agents = useMapGetter('agents/getAgents');

const linkedTickets = ref([]);
const ticketTypes = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const showCreate = ref(false);
const form = ref({});
const selectedTicket = ref(null);

const backOfficeTypes = computed(() =>
  ticketTypes.value.filter(type => type.category === 'back_office')
);
const selectedTicketType = computed(() =>
  ticketTypes.value.find(
    type => type.id === selectedTicket.value?.ticket_type.id
  )
);

const resetForm = () => {
  const senderName = currentChat.value.meta?.sender?.name || '';
  form.value = {
    ticket_type_id: backOfficeTypes.value[0]?.id || '',
    source_conversation_id: props.conversationId,
    title: senderName
      ? t('BACK_OFFICE_TICKETS.DEFAULT_TITLE', { name: senderName })
      : '',
    description: '',
    priority: currentChat.value.priority || 'normal',
    team_id: currentChat.value.meta?.team?.id || '',
    assignee_id: currentChat.value.meta?.assignee?.id || '',
  };
};

const fetchTickets = async () => {
  const { data } = await TicketsAPI.getForConversation(
    props.conversationId,
    'back_office'
  );
  linkedTickets.value = data;
};

const fetchTypes = async () => {
  const { data } = await TicketTypesAPI.get();
  ticketTypes.value = data;
};

const openCreateTicket = () => {
  resetForm();
  showCreate.value = true;
};

const closeCreateTicket = () => {
  showCreate.value = false;
};

const openTicket = ticket => {
  selectedTicket.value = ticket;
};

const updateTicket = ticket => {
  const index = linkedTickets.value.findIndex(item => item.id === ticket.id);
  if (index !== -1) linkedTickets.value[index] = ticket;
  selectedTicket.value = ticket;
};

const createTicket = async () => {
  isSaving.value = true;
  try {
    await TicketsAPI.create({ ticket: form.value });
    await fetchTickets();
    closeCreateTicket();
    useAlert(t('BACK_OFFICE_TICKETS.CREATE_SUCCESS'));
  } catch (error) {
    useAlert(t('BACK_OFFICE_TICKETS.CREATE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

onMounted(async () => {
  isLoading.value = true;
  store.dispatch('teams/get');
  store.dispatch('agents/get');
  try {
    await Promise.all([fetchTickets(), fetchTypes()]);
  } catch (error) {
    useAlert(t('BACK_OFFICE_TICKETS.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <section class="border-b border-n-weak px-3 py-3">
    <div class="flex items-center justify-between gap-3">
      <div>
        <h3 class="m-0 text-sm font-medium text-n-slate-12">
          {{ $t('BACK_OFFICE_TICKETS.TITLE') }}
        </h3>
        <p class="mb-0 text-xs text-n-slate-10">
          {{ $t('BACK_OFFICE_TICKETS.DESCRIPTION') }}
        </p>
      </div>
      <Button
        :label="$t('BACK_OFFICE_TICKETS.CREATE')"
        icon="i-lucide-plus"
        size="xs"
        variant="ghost"
        color="slate"
        @click="openCreateTicket"
      />
    </div>

    <div
      v-if="linkedTickets.length"
      class="mt-3 divide-y divide-n-weak border-y border-n-weak"
    >
      <button
        v-for="ticket in linkedTickets"
        :key="ticket.id"
        type="button"
        class="flex w-full items-center gap-2 py-2 text-left hover:bg-n-alpha-1"
        @click="openTicket(ticket)"
      >
        <span
          class="flex size-7 shrink-0 items-center justify-center rounded-md bg-n-alpha-2"
        >
          <Icon icon="i-lucide-lock-keyhole" class="size-3.5 text-n-slate-11" />
        </span>
        <span class="min-w-0 flex-1">
          <span class="block truncate text-sm font-medium text-n-slate-12">{{
            ticket.title
          }}</span>
          <span class="block truncate text-xs text-n-slate-10">
            {{
              $t('BACK_OFFICE_TICKETS.STATUS_LINE', {
                state: ticket.ticket_state.name,
                team: ticket.team?.name || $t('BACK_OFFICE_TICKETS.UNASSIGNED'),
              })
            }}
          </span>
        </span>
        <Icon icon="i-lucide-chevron-right" class="size-3.5 text-n-slate-9" />
      </button>
    </div>
    <p v-else-if="!isLoading" class="mb-0 mt-2 text-xs text-n-slate-10">
      {{ $t('BACK_OFFICE_TICKETS.EMPTY') }}
    </p>
  </section>

  <div
    v-if="showCreate"
    class="fixed inset-0 z-50 flex justify-end bg-n-alpha-4"
    @click.self="closeCreateTicket"
  >
    <form
      class="flex h-full w-full max-w-lg flex-col bg-n-surface-1 shadow-xl"
      @submit.prevent="createTicket"
    >
      <header
        class="flex items-center justify-between border-b border-n-weak px-5 py-4"
      >
        <div>
          <h2 class="text-lg font-semibold text-n-slate-12">
            {{ $t('BACK_OFFICE_TICKETS.DRAWER_TITLE') }}
          </h2>
          <p class="mb-0 text-sm text-n-slate-10">
            {{ $t('BACK_OFFICE_TICKETS.DRAWER_DESCRIPTION') }}
          </p>
        </div>
        <Button
          icon="i-lucide-x"
          variant="ghost"
          color="slate"
          type="button"
          @click="closeCreateTicket"
        />
      </header>

      <div class="flex-1 space-y-4 overflow-y-auto px-5 py-5">
        <div v-if="!backOfficeTypes.length" class="border-y border-n-weak py-5">
          <p class="mb-3 text-sm text-n-slate-11">
            {{ $t('BACK_OFFICE_TICKETS.NO_TYPES') }}
          </p>
          <router-link
            :to="{ name: 'ticket_types_list' }"
            class="text-sm font-medium text-n-blue-11 hover:underline"
          >
            {{ $t('BACK_OFFICE_TICKETS.CONFIGURE_TYPES') }}
          </router-link>
        </div>
        <template v-else>
          <label class="block space-y-1.5 text-sm text-n-slate-11">
            <span>{{ $t('BACK_OFFICE_TICKETS.TYPE') }}</span>
            <select
              v-model="form.ticket_type_id"
              required
              class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12"
            >
              <option
                v-for="type in backOfficeTypes"
                :key="type.id"
                :value="type.id"
              >
                {{ type.name }}
              </option>
            </select>
          </label>
          <label class="block space-y-1.5 text-sm text-n-slate-11">
            <span>{{ $t('BACK_OFFICE_TICKETS.TICKET_TITLE') }}</span>
            <input
              v-model="form.title"
              required
              maxlength="255"
              class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12 outline-none focus:border-n-brand"
            />
          </label>
          <label class="block space-y-1.5 text-sm text-n-slate-11">
            <span>{{ $t('BACK_OFFICE_TICKETS.TICKET_DESCRIPTION') }}</span>
            <textarea
              v-model="form.description"
              rows="5"
              maxlength="10000"
              class="w-full resize-none rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-n-slate-12 outline-none focus:border-n-brand"
            />
          </label>
          <div class="grid grid-cols-2 gap-3">
            <label class="space-y-1.5 text-sm text-n-slate-11">
              <span>{{ $t('BACK_OFFICE_TICKETS.PRIORITY') }}</span>
              <select
                v-model="form.priority"
                class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12"
              >
                <option value="normal">
                  {{ $t('BACK_OFFICE_TICKETS.PRIORITIES.NORMAL') }}
                </option>
                <option value="low">
                  {{ $t('BACK_OFFICE_TICKETS.PRIORITIES.LOW') }}
                </option>
                <option value="high">
                  {{ $t('BACK_OFFICE_TICKETS.PRIORITIES.HIGH') }}
                </option>
                <option value="urgent">
                  {{ $t('BACK_OFFICE_TICKETS.PRIORITIES.URGENT') }}
                </option>
              </select>
            </label>
            <label class="space-y-1.5 text-sm text-n-slate-11">
              <span>{{ $t('BACK_OFFICE_TICKETS.TEAM') }}</span>
              <select
                v-model="form.team_id"
                class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 capitalize text-n-slate-12"
              >
                <option value="">
                  {{ $t('BACK_OFFICE_TICKETS.UNASSIGNED') }}
                </option>
                <option v-for="team in teams" :key="team.id" :value="team.id">
                  {{ team.name }}
                </option>
              </select>
            </label>
          </div>
          <label class="block space-y-1.5 text-sm text-n-slate-11">
            <span>{{ $t('BACK_OFFICE_TICKETS.ASSIGNEE') }}</span>
            <select
              v-model="form.assignee_id"
              class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12"
            >
              <option value="">
                {{ $t('BACK_OFFICE_TICKETS.UNASSIGNED') }}
              </option>
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                {{ agent.name }}
              </option>
            </select>
          </label>
        </template>
      </div>

      <footer class="flex justify-end gap-2 border-t border-n-weak px-5 py-4">
        <Button
          :label="$t('BACK_OFFICE_TICKETS.CANCEL')"
          variant="ghost"
          color="slate"
          type="button"
          @click="closeCreateTicket"
        />
        <Button
          v-if="backOfficeTypes.length"
          :label="$t('BACK_OFFICE_TICKETS.SUBMIT')"
          :is-loading="isSaving"
          type="submit"
        />
      </footer>
    </form>
  </div>

  <TicketLifecycleDrawer
    v-if="selectedTicket && selectedTicketType"
    :ticket="selectedTicket"
    :ticket-type="selectedTicketType"
    @close="selectedTicket = null"
    @saved="updateTicket"
  />
</template>
